// ============================================
// FILE: src/components/Reviews.jsx
// ============================================
import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// ─── CONFIG: adjust card size here ───────────────────────────
const CARD_WIDTH  = 320;   // px — width of each card
const CARD_HEIGHT = 320;   // px — height of each card
const CARD_GAP    = 20;    // px — gap between cards
const CARDS_VISIBLE = 5;   // how many cards show at once (desktop)
const AUTO_SLIDE_INTERVAL = 3000; // ms — auto slide every 3 seconds
// ─────────────────────────────────────────────────────────────

// Drop your image paths / URLs here
const IMAGES = [
  { id: 1, src: '/images/Reviews/img1.png', alt: 'Property 1' },
  { id: 2, src: '/images/Reviews/img2.png', alt: 'Property 2' },
  { id: 3, src: '/images/Reviews/img3.png', alt: 'Property 3' },
  { id: 4, src: '/images/Reviews/img4.png', alt: 'Property 4' },
  { id: 5, src: '/images/Reviews/img5.png', alt: 'Property 5' },
  { id: 6, src: '/images/Reviews/img2.png', alt: 'Property 6' },
    { id: 9, src: '/images/Reviews/img6.png', alt: 'Property 9' },
  { id: 7, src: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=800&h=600&fit=crop', alt: 'Property 7' },
  { id: 8, src: 'https://images.unsplash.com/photo-1600607687644-c7171b42498f?w=800&h=600&fit=crop', alt: 'Property 8' },
];

const STYLES = `
  .reviews-carousel-wrap {
    width: 100%;
    overflow: hidden;
    position: relative;
  }

  .reviews-track {
    display: flex;
    gap: ${CARD_GAP}px;
    transition: transform 0.45s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: transform;
  }

  .review-card {
    flex-shrink: 0;
    width: ${CARD_WIDTH}px;
    height: ${CARD_HEIGHT}px;
    border-radius: 18px;
    overflow: hidden;
    position: relative;
    box-shadow: 0 4px 24px rgba(0,0,0,0.10);
    transition: transform 0.35s ease, box-shadow 0.35s ease;
    cursor: pointer;
    background: #e5e7eb;
  }

  .review-card:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 20px 48px rgba(0,0,0,0.18);
  }

  .review-card img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    transition: transform 0.5s ease;
  }

  .review-card:hover img {
    transform: scale(1.06);
  }

  .carousel-nav-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    box-shadow: 0 4px 20px rgba(0,0,0,0.14);
    transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
    color: #1a1208;
  }

  .carousel-nav-btn:hover {
    background: #0f1f3d;
    color: #c9a84c;
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 8px 28px rgba(0,0,0,0.2);
  }

  .carousel-nav-btn:disabled {
    opacity: 0.35;
    cursor: default;
    pointer-events: none;
  }

  .carousel-dots {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 24px;
  }

  .carousel-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d1d5db;
    border: none;
    cursor: pointer;
    padding: 0;
    transition: background 0.25s, width 0.25s;
  }

  .carousel-dot.active {
    background: #0f1f3d;
    width: 24px;
    border-radius: 4px;
  }
`;

const Reviews = () => {
  const [index, setIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const maxIndex = Math.max(0, IMAGES.length - CARDS_VISIBLE);
  const intervalRef = useRef(null);

  // Auto-slide function
  const goToNextSlide = () => {
    setIndex(prevIndex => {
      if (prevIndex >= maxIndex) {
        return 0; // Loop back to start
      }
      return prevIndex + 1;
    });
  };

  // Start auto-slide
  const startAutoSlide = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(goToNextSlide, AUTO_SLIDE_INTERVAL);
    setIsAutoPlaying(true);
  };

  // Stop auto-slide
  const stopAutoSlide = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsAutoPlaying(false);
  };

  // Reset auto-slide timer
  const resetAutoSlide = () => {
    stopAutoSlide();
    startAutoSlide();
  };

  // Manual navigation handlers
  const prev = () => {
    setIndex(i => Math.max(0, i - 1));
    resetAutoSlide();
  };

  const next = () => {
    setIndex(i => Math.min(maxIndex, i + 1));
    resetAutoSlide();
  };

  // Start auto-slide on mount
  useEffect(() => {
    startAutoSlide();
    return () => stopAutoSlide();
  }, []);

  // Pause auto-slide when user interacts with carousel
  useEffect(() => {
    const handleMouseEnter = () => stopAutoSlide();
    const handleMouseLeave = () => startAutoSlide();

    const carouselContainer = document.querySelector('.reviews-carousel-wrap');
    if (carouselContainer) {
      carouselContainer.addEventListener('mouseenter', handleMouseEnter);
      carouselContainer.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (carouselContainer) {
        carouselContainer.removeEventListener('mouseenter', handleMouseEnter);
        carouselContainer.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);

  const offset = index * (CARD_WIDTH + CARD_GAP);

  return (
    <section style={{ padding: '72px 0', background: '#f8f6f0' }}>
      <style>{STYLES}</style>

      {/* ── Heading ── */}
      <div style={{ textAlign: 'center', marginBottom: '44px', padding: '0 24px' }}>
        <p style={{ fontSize: '12px', fontWeight: '700', letterSpacing: '0.12em', color: '#c9a84c', textTransform: 'uppercase', marginBottom: '10px' }}>
          Our Portfolio
        </p>
        <h2 style={{ fontSize: 'clamp(30px,5vw,52px)', fontWeight: '800', color: '#0f1f3d', margin: 0, lineHeight: 1.1, fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
        Featured Properties & <em style={{ fontStyle: 'italic', color: '#c9a84c' }}>Happy Clients</em>
        </h2>
      </div>

      {/* ── Carousel ── */}
      <div style={{ position: 'relative', padding: '0 64px' }}>

        {/* Prev */}
        <button
          className="carousel-nav-btn"
          style={{ left: '12px' }}
          onClick={prev}
          disabled={index === 0}
          aria-label="Previous"
        >
          <ChevronLeft size={22} />
        </button>

        {/* Track wrapper (clips overflow) */}
        <div className="reviews-carousel-wrap">
          <div
            className="reviews-track"
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {IMAGES.map(img => (
              <div key={img.id} className="review-card">
                <img src={img.src} alt={img.alt} loading="lazy" />
              </div>
            ))}
          </div>
        </div>

        {/* Next */}
        <button
          className="carousel-nav-btn"
          style={{ right: '12px' }}
          onClick={next}
          disabled={index >= maxIndex}
          aria-label="Next"
        >
          <ChevronRight size={22} />
        </button>
      </div>

      {/* ── Dots ── */}
      <div className="carousel-dots">
        {Array.from({ length: maxIndex + 1 }).map((_, i) => (
          <button
            key={i}
            className={`carousel-dot ${i === index ? 'active' : ''}`}
            onClick={() => {
              setIndex(i);
              resetAutoSlide();
            }}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default Reviews;